import type React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import { Button, Container, SpacerRow } from '@zg-rentals/multibrand-ui';
import Section from '@rental-manager/components/Section';
import Modal from '@common/components/Modal';
import userPersonas from '@common/constants/userPersonas';
import FeaturePermissionTypes from '@rental-manager/constants/FeaturePermissionTypes';
import ProblemTriangle from '@zg-rentals/multibrand-ui/lib/icons/ProblemTriangle';
import ExpandableDisclaimer from '@common/components/ExpandableDisclaimer';
import type { IStyledTextModel } from '@rental-manager/models/Types';
import StringConstants from '@common/constants/StringConstants';
import DebouncedButton from '@common/components/DebouncedButton';

type RentalProtectionDisclaimers = Array<IStyledTextModel>;

type Props = {
  onClose: () => void;
  skipRentalProtectionOnboarding: ({ featureName, on }) => void;
  onBack: () => void;
  rentalProtectionDisclaimers: RentalProtectionDisclaimers;
  propertyId: string;
};

const UnsignedLeaseWarning: React.FC<React.PropsWithChildren<Props>> = (props: Props): React.ReactElement => {
  const skipRentalProtectionOnboarding = () => {
    props.skipRentalProtectionOnboarding({
      featureName: FeaturePermissionTypes.RENT_GUARANTEE,
      on: false,
    });
  };

  const backToLeases = () => {
    const { propertyId } = props;
    window.location.href = StringConstants.LINKS.GO_TO_LEASES(propertyId);
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      header="Payment setup"
      onRequestClose={props.onClose}
      onClickBack={props.onBack}
      dataAutoTestId="landlord-rental-protection-unsigned-lease-warning"
    >
      {
        <Container>
          <Section>
            <ProblemTriangle className="centeredImg" colorType="action" size="lg" />
          </Section>
          <Section>
            <Text type="h4" className="txtC zillowInsuranceSubHeading" emphasize>
              Zillow Insurance Services
            </Text>
            <Text type="h3" className="txtC boldText">
              This lease is missing signatures
            </Text>
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <Text>The lease you selected is not signed by either you and/or your renter(s).</Text>
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <Text>
              Rental Protection requires <strong>the lease to be signed by all parties</strong> before payments are set
              up.
            </Text>
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <Text>
              If you continue to set up payments with an unsigned lease, you will <strong>not</strong> be able to
              purchase Rental Protection for this property.
            </Text>
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <ExpandableDisclaimer disclaimerContent={props.rentalProtectionDisclaimers} title="Legal Disclaimer" />
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <Button onClick={backToLeases} type="primary" full>
              Back to leases
            </Button>
          </Section>
          <Section>
            <DebouncedButton onClick={skipRentalProtectionOnboarding} type="secondary" full>
              Continue to payments
            </DebouncedButton>
          </Section>
        </Container>
      }
    </Modal>
  );
};

export default UnsignedLeaseWarning;
